/**
 * D-319 (part A) - the new monthly approval: pure helpers (no React), shared by the blocking window,
 * the "היום שלי" card, the 3-step approval page, the timer and the manager's table.
 */

export interface PendingMonth {
  /** YYYY-MM */
  month: string;
  logs_required: boolean;
  reopened: boolean;
  /** D-333: attendance already approved - only the logs step is left. */
  attendance_done?: boolean;
}

export interface MonthApprovalStatus {
  pending: PendingMonth[];
  first_of_month: boolean;
  current_month: string;
  message: string | null;
}

export interface MonthLog {
  id: string;
  ticket_id: string;
  ticket_title: string | null;
  ticket_number: number | null;
  organization_id: string | null;
  organization_name: string | null;
  engagement_id: string | null;
  start_time: string;
  end_time: string | null;
  duration_minutes: number;
  description: string | null;
  is_service: boolean;
  is_long: boolean;
  /** YYYY-MM-DD (Israel) */
  day: string;
}

export interface CalendarDay {
  date: string;
  name: string | null;
  kind: 'holiday' | 'eve' | 'day_off' | 'note_only';
  isWorkday: boolean;
}

export interface MonthLogsResponse {
  month: string;
  logs_required: boolean;
  long_minutes: number;
  logs: MonthLog[];
  calendar: CalendarDay[];
}

export type EmployeeMonthStatus = 'in_progress' | 'pending' | 'approved' | 'reopened' | 'logs_pending';

export interface MonthApprovalOverview {
  month: string;
  ended: boolean;
  employees: Array<{ user_id: string; name: string; status: EmployeeMonthStatus; approved_at: string | null; in_mail: boolean }>;
  approved: number;
  total: number;
  pending_count: number;
  logs_pending_count?: number;
  applies: boolean;
  mail: { batch: { status: string; sent_at: string | null; count: number } | null; supplements: number; last_failed: boolean };
}

/** The first month the new approval applies to (the server's D319_FIRST_MONTH). */
export const D319_FIRST_MONTH = '2026-10';

/** The route of the approval flow for a month. */
export function monthApprovalPath(month: string): string {
  return `/month-approval/${month}`;
}

/**
 * The blocking window shows on every page while a month is pending, except the approval page itself and
 * "היום שלי" (the home page shows the 1st-of-month card with the same button, and the clock-in stays
 * possible there - D-319 s.4/s.6).
 */
export function gateShowsOn(pathname: string, status: MonthApprovalStatus | null | undefined): boolean {
  if (!status || status.pending.length === 0) return false;
  if (pathname.startsWith('/month-approval')) return false;
  if (pathname === '/' || pathname === '') return false;
  return true;
}

/** The server refused a timer start because a month is not approved (D-319 s.6, 409). */
export function isMonthApprovalRequired(err: unknown): boolean {
  const e = err as { error?: { error_code?: string }; response?: { data?: { error?: { error_code?: string } } } } | null;
  return (e?.error?.error_code ?? e?.response?.data?.error?.error_code) === 'MONTH_APPROVAL_REQUIRED';
}

/** The month the refusal names (or null). */
export function monthOfApprovalError(err: unknown): string | null {
  const e = err as { error?: { month?: string }; response?: { data?: { error?: { month?: string } } } } | null;
  const m = e?.error?.month ?? e?.response?.data?.error?.month;
  return typeof m === 'string' && /^\d{4}-\d{2}$/.test(m) ? m : null;
}

/** Minutes -> "h:mm". */
export function hm(minutes: number): string {
  const m = Math.max(0, Math.round(Number(minutes) || 0));
  return `${Math.floor(m / 60)}:${String(m % 60).padStart(2, '0')}`;
}

export interface LogGroup {
  key: string;
  label: string;
  minutes: number;
  logs: MonthLog[];
}

/** "לפי ימים": one group per day (in order); "לפי לקוח": one per customer (by name). */
export function groupMonthLogs(logs: MonthLog[], by: 'day' | 'customer', noCustomerLabel = '—'): LogGroup[] {
  const map = new Map<string, LogGroup>();
  for (const l of logs) {
    const key = by === 'day' ? l.day : (l.organization_id ?? 'none');
    const label = by === 'day' ? l.day : (l.organization_name || noCustomerLabel);
    let g = map.get(key);
    if (!g) { g = { key, label, minutes: 0, logs: [] }; map.set(key, g); }
    g.logs.push(l);
    g.minutes += Number(l.duration_minutes) || 0;
  }
  const out = [...map.values()];
  return by === 'day' ? out.sort((a, b) => a.key.localeCompare(b.key)) : out.sort((a, b) => a.label.localeCompare(b.label, 'he'));
}

/** The customers of the month (for the "לקוח: כולם ▾" filter). */
export function customerOptions(logs: MonthLog[], noCustomerLabel = '—'): Array<{ value: string; label: string }> {
  const map = new Map<string, string>();
  for (const l of logs) map.set(l.organization_id ?? 'none', l.organization_name || noCustomerLabel);
  return [...map.entries()].map(([value, label]) => ({ value, label })).sort((a, b) => a.label.localeCompare(b.label, 'he'));
}

export function filterMonthLogs(logs: MonthLog[], f: { customer?: string; day?: string }): MonthLog[] {
  return logs.filter((l) =>
    (!f.customer || (l.organization_id ?? 'none') === f.customer) && (!f.day || l.day === f.day));
}

/** The long logs (> 7 h) not yet marked "כן, הלוג נכון": the approval waits for them. */
export function unconfirmedLongLogs(logs: MonthLog[], confirmed: ReadonlySet<string>): MonthLog[] {
  return logs.filter((l) => l.is_long && !confirmed.has(l.id));
}

/** FIX-LONGLOG-UI "סימון הכל": every long log of the month marked correct (same set the single checkbox feeds). */
export function markAllLong(logs: MonthLog[], confirmed: ReadonlySet<string>): Set<string> {
  const next = new Set(confirmed);
  for (const l of logs) if (l.is_long) next.add(l.id);
  return next;
}

/**
 * The approval steps of a month. D-333: the logs are part of the approval from October 2026; whoever
 * already approved the attendance (attendanceDone) goes straight to the logs step.
 */
export function approvalSteps(logsRequired: boolean, attendanceDone = false): Array<'attendance' | 'logs' | 'confirm'> {
  if (!logsRequired) return ['attendance', 'confirm'];
  return attendanceDone ? ['logs', 'confirm'] : ['attendance', 'logs', 'confirm'];
}

/** The tone of a status in the manager's table (D-319 s.3). */
export function statusTone(status: EmployeeMonthStatus): 'neutral' | 'warning' | 'success' | 'danger' {
  return status === 'approved' ? 'success' : status === 'pending' || status === 'logs_pending' ? 'warning' : status === 'reopened' ? 'danger' : 'neutral';
}

/**
 * FIX-ALLDAYS - the attendance step lists EVERY day of the month except Friday and Saturday, so an
 * employee who did not clock in on a day can see it and add it. A Friday / Saturday is listed only
 * when it has attendance. Status of a day:
 *  - 'worked'  - it has attendance (a row of the month report);
 *  - 'off'     - no attendance on a calendar holiday / day off for all (not counted as missing);
 *  - 'future'  - no attendance, after today (not missing yet, cannot be edited);
 *  - 'missing' - a past workday with no attendance ("לא דווחה נוכחות").
 * An eve / a "note only" day is a regular day (the caller shows its name as a label).
 */
export type ApprovalDayStatus = 'worked' | 'off' | 'future' | 'missing';

export interface ApprovalDay<R> {
  date: string;
  row: R | null;
  cal: CalendarDay | null;
  /** The calendar says holiday / day off for all (the row is colored, whatever the status). */
  isOff: boolean;
  status: ApprovalDayStatus;
}

export function buildApprovalDays<R extends { date: string }>(
  month: string,
  rows: R[],
  calendar: Map<string, CalendarDay> | CalendarDay[],
  today: string
): ApprovalDay<R>[] {
  if (!/^\d{4}-(0[1-9]|1[0-2])$/.test(month)) return [];
  const cal = calendar instanceof Map ? calendar : new Map(calendar.map((d) => [d.date, d]));
  const byDate = new Map(rows.map((r) => [r.date, r]));
  const [y, m] = month.split('-').map(Number);
  const last = new Date(Date.UTC(y, m, 0)).getUTCDate();
  const out: ApprovalDay<R>[] = [];
  for (let d = 1; d <= last; d++) {
    const date = `${month}-${String(d).padStart(2, '0')}`;
    const wd = new Date(Date.UTC(y, m - 1, d)).getUTCDay(); // 5 = Friday, 6 = Saturday
    const row = byDate.get(date) ?? null;
    if (!row && (wd === 5 || wd === 6)) continue;
    const c = cal.get(date) ?? null;
    const isOff = !!c && c.isWorkday === false;
    const status: ApprovalDayStatus = row ? 'worked' : isOff ? 'off' : date > today ? 'future' : 'missing';
    out.push({ date, row, cal: c, isOff, status });
  }
  return out;
}
